import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useSelector } from "react-redux";
import DynamicLoader from "../Form/FormFields/DynamicLoader";
import Accordion from "../ui/Accordion";
import { config } from "../../SampleData/CombinedChartData";
import LabelField from "../Form/FormFields/LabelField";
import NoteComponent from "../ui/table/Notes";

interface DynamicCombinedChartProps {
  chartListData: { id: string; title: string };
  width?: string;
}

const DynamicCombinedTwoColumnsTwoLinesChart: React.FC<
  DynamicCombinedChartProps
> = ({ chartListData, width }) => {
  const selectorData = useSelector((state: any) => state.dynamicData);
  const notes = chartListData?.note;
  const noteData = chartListData?.remarks;
  const chartData = selectorData[chartListData.id] || [];
  const isLoading = selectorData.loaders[`report/chart/${chartListData.id}`];

  // ✅ Safe config fallback logic
  const chartConfig =
    Array.isArray(chartData?.label_name) && chartData.label_name.length > 0
      ? chartData.label_name[0]
      : config[0];

  const xKey = "x";

  // find y keysy
  const yKeys = Object.keys(chartConfig).filter((key) => key !== xKey);

  // x-axis categories
  const categories =
    chartData && chartData?.data?.map((item: any) => item[xKey]);

  // find unique titles dynamically
  const uniqueTitles = [...new Set(yKeys.map((k) => chartConfig[k].title))];

  // create dynamic yAxis (alternate sides)
  const yAxis: Highcharts.YAxisOptions[] = uniqueTitles.map(
    (title: any, i: number) => ({
      title: { text: title || "" },
      opposite: i % 2 === 1, // alternate left/right
    })
  );

  const series: any = yKeys.map((key) => {
    const field = chartConfig[key];
    const titleIndex = uniqueTitles.indexOf(field.title);
    return {
      name: field.name,
      type: field.type || "line",
      yAxis: titleIndex,
      data: chartData && chartData?.data?.map((d: any) => Number(d[key])),
      tooltip: { valueSuffix: field.title ? ` ${field.title}` : "" },
    };
  });

  const options: Highcharts.Options = {
    chart: {
      zooming: { type: "xy" },
      backgroundColor: "transparent",
    },
    title: {
      text: "",
      align: "left",
      style: { fontSize: "16px", fontWeight: "600" },
    },
    xAxis: [
      { categories, title: { text: chartConfig[xKey].title || "X-Axis" } },
    ],
    yAxis,
    tooltip: { shared: true },
    legend: { align: "left", verticalAlign: "top" },
    series,
    credits: { enabled: false },
  };

  return (
    <div className={`grid gap-2 ${width && width}`}>
      <div className="bg-white p-4 rounded-lg w-full">
        <h2 className="text-lg font-semibold mb-2">
          <div className="flex justify-between items-center mb-1">
            <div className="flex items-center">
              <div
                className="text-[#464E5F] text-[14px] mb-0 pb-0 font-semibold"
                style={{ marginBottom: "0 !important" }}
              >
                <LabelField label={chartListData?.title} />
              </div>
              {notes && (
                <NoteComponent
                  notes={noteData}
                  modalTitle={chartListData?.title}
                />
              )}
            </div>
          </div>
        </h2>
        {isLoading ? (
          <DynamicLoader loading={isLoading} />
        ) : (
          <>
            <div className="w-full" style={{ height: 350 }}>
              <HighchartsReact highcharts={Highcharts} options={options} />
            </div>
            <div className="p-2 mt-12">
              <Accordion
                title="AI Insights"
                componentID={chartListData.id}
                chartData={chartListData}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DynamicCombinedTwoColumnsTwoLinesChart;
